import {
  ApiError,
  queryKeys,
  WIKI_PAGE_CONTENT_MAX,
  type WikiPage as WikiPageType,
  type WikiSection,
} from '@mevaser/shared'
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useToast,
} from '@mevaser/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams } from '@tanstack/react-router'
import { ArrowLeft, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { Page, PageHeader } from '../components/app-shell.js'
import {
  AttachButton,
  AttachmentChips,
  AttachmentList,
  inlineImageUploader,
  useFileUploads,
} from '../components/attachments.js'
import { RichTextEditor } from '../components/rich-text-editor.js'
import { api } from '../lib/api.js'
import { useAuth } from '../lib/auth.js'
import { PageAccessCard } from './wiki-page-access.js'

/**
 * The page editor. Title/section/content save through the general `PATCH`
 * (`wiki:write`); publishing is a separate action (`wiki:manage`) that the
 * API keeps out of that patch on purpose — see `updateWikiPageSchema` in
 * `packages/shared/src/schemas/wiki.ts`.
 */
export function WikiPageDetailPage() {
  const { pageId } = useParams({ from: '/authed/wiki/$pageId' })
  const { can } = useAuth()
  const canWrite = can('wiki:write')
  const canManage = can('wiki:manage')
  const navigate = useNavigate()
  const toast = useToast()
  const queryClient = useQueryClient()

  const { data: page, isLoading } = useQuery({
    queryKey: queryKeys.wiki.page(pageId),
    queryFn: () => api.get<WikiPageType>(`/wiki/pages/${pageId}`),
  })
  const { data: sections } = useQuery({
    queryKey: queryKeys.wiki.sections(),
    queryFn: () => api.get<WikiSection[]>('/wiki/sections'),
  })

  const [title, setTitle] = useState('')
  const [sectionId, setSectionId] = useState('')
  const [contentHtml, setContentHtml] = useState<string | null>('')
  const uploads = useFileUploads({ endpoint: '/attachments?for=wiki' })

  // `page` seeds the form the first time it's available for this page id —
  // in render, not an effect, so the rich-text editor below mounts with the
  // real content instead of mounting empty and seeding a beat too late (it
  // only reads its `content` prop at mount). Keyed by id rather than object
  // identity so typing afterwards isn't clobbered by a background refetch.
  const [seededPageId, setSeededPageId] = useState<string | null>(null)
  if (page && page.id !== seededPageId) {
    setSeededPageId(page.id)
    setTitle(page.title)
    setSectionId(page.sectionId)
    setContentHtml(page.contentHtml)
    uploads.reset(page.attachments)
  }

  const [deleting, setDeleting] = useState(false)

  const section = sections?.find((s) => s.id === sectionId) ?? null
  const dirty = page
    ? title !== page.title ||
      sectionId !== page.sectionId ||
      (contentHtml ?? '') !== page.contentHtml ||
      [...uploads.ids].sort().join() !==
        page.attachments
          .map((a) => a.id)
          .sort()
          .join()
    : false

  const save = useMutation({
    mutationFn: () =>
      api.patch<WikiPageType>(`/wiki/pages/${pageId}`, {
        title,
        sectionId,
        contentHtml: contentHtml ?? '',
        attachmentIds: uploads.ids,
      }),
    onSuccess: async (updated) => {
      queryClient.setQueryData(queryKeys.wiki.page(pageId), updated)
      // The server sanitizes saved HTML (e.g. strips attributes Tiptap adds
      // to links), so the saved value can differ slightly from what was
      // sent — re-seed from it or `dirty` would never clear.
      setTitle(updated.title)
      setSectionId(updated.sectionId)
      setContentHtml(updated.contentHtml)
      uploads.reset(updated.attachments)
      await queryClient.invalidateQueries({ queryKey: queryKeys.wiki.all() })
      toast.success('Saved')
    },
    onError: (error: Error) =>
      toast.error('Could not save', error instanceof ApiError ? error.message : undefined),
  })

  const publish = useMutation({
    mutationFn: (published: boolean) =>
      api.post<WikiPageType>(`/wiki/pages/${pageId}/publish`, { published }),
    onSuccess: async (updated) => {
      queryClient.setQueryData(queryKeys.wiki.page(pageId), updated)
      await queryClient.invalidateQueries({ queryKey: queryKeys.wiki.all() })
      toast.success(updated.isPublished ? 'Published' : 'Unpublished')
    },
    onError: (error: Error) =>
      toast.error(
        'Could not change publish status',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  const remove = useMutation({
    mutationFn: () => api.delete(`/wiki/pages/${pageId}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.wiki.all() })
      toast.success('Page deleted')
      await navigate({ to: '/wiki' })
    },
    onError: (error: Error) =>
      toast.error('Could not delete', error instanceof ApiError ? error.message : undefined),
  })

  if (isLoading) {
    return (
      <Page>
        <div className="h-40 animate-pulse rounded-lg bg-surface-sunken" />
      </Page>
    )
  }

  if (!page) {
    return (
      <Page>
        <PageHeader title="Page not found" description="It may have been deleted." />
      </Page>
    )
  }

  const isLive = page.isPublished && section?.visibility === 'public' && !page.restricted

  return (
    <Page>
      <button
        type="button"
        onClick={() => void navigate({ to: '/wiki' })}
        className="mb-3 inline-flex items-center gap-1 text-sm text-foreground-muted transition-colors hover:text-foreground"
      >
        <ArrowLeft className="size-3.5" /> Wiki
      </button>

      <PageHeader
        title={page.title}
        description={
          isLive
            ? 'Published — live on the public knowledge base.'
            : page.restricted
              ? 'Restricted — only the people, roles and guests granted below can open it.'
              : page.isPublished
                ? 'Published, but its section is internal, so it is not visible on the website.'
                : 'Draft — not visible outside the back office.'
        }
        actions={
          <div className="flex items-center gap-2">
            {canManage ? (
              <Button
                variant={page.isPublished ? 'secondary' : 'primary'}
                onClick={() => publish.mutate(!page.isPublished)}
                disabled={publish.isPending}
              >
                {publish.isPending ? 'Working…' : page.isPublished ? 'Unpublish' : 'Publish'}
              </Button>
            ) : null}
            {canManage ? (
              <Button
                variant="ghost"
                size="icon"
                aria-label="Delete page"
                onClick={() => setDeleting(true)}
              >
                <Trash2 />
              </Button>
            ) : null}
          </div>
        }
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_280px]">
        <Card>
          <CardContent className="space-y-4 pt-5">
            <Field label="Title" required>
              {(props) => (
                <Input
                  {...props}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  disabled={!canWrite}
                />
              )}
            </Field>
            <RichTextEditor
              // Tiptap only reads its initial content at mount, unlike a
              // plain controlled input — remount on the page id so
              // switching which page is being edited doesn't leave the
              // previous one's content showing.
              key={page.id}
              value={contentHtml}
              onChange={setContentHtml}
              headings
              links
              maxLength={WIKI_PAGE_CONTENT_MAX}
              uploadImage={canWrite ? inlineImageUploader('wiki') : undefined}
            />
            {canWrite ? (
              <div className="space-y-2">
                <AttachmentChips uploads={uploads} />
                <AttachButton uploads={uploads} />
                <p className="text-xs text-foreground-muted">
                  Files you attach are listed under the page for readers to download. To put a
                  picture in the text itself, paste it into the page.
                </p>
              </div>
            ) : (
              <AttachmentList files={page.attachments} />
            )}
          </CardContent>
        </Card>

        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Section</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Select value={sectionId} onValueChange={setSectionId} disabled={!canWrite}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(sections ?? []).map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {section ? (
                <Badge tone={section.visibility === 'public' ? 'success' : 'neutral'}>
                  {section.visibility === 'public' ? 'Public section' : 'Internal section'}
                </Badge>
              ) : null}
              {isLive ? (
                <p className="break-all text-xs text-foreground-muted">
                  Live at{' '}
                  <span className="font-identifier">
                    /kb/{section?.slug}/{page.slug}
                  </span>
                </p>
              ) : null}
            </CardContent>
          </Card>

          {canManage ? <PageAccessCard pageId={pageId} /> : null}

          {canWrite ? (
            <Button
              variant="primary"
              className="w-full"
              onClick={() => save.mutate()}
              disabled={save.isPending || !dirty}
            >
              {save.isPending ? 'Saving…' : 'Save changes'}
            </Button>
          ) : null}
        </div>
      </div>

      <Dialog open={deleting} onOpenChange={setDeleting}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete "{page.title}"?</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <p className="text-sm">This cannot be undone.</p>
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleting(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={() => remove.mutate()} disabled={remove.isPending}>
              {remove.isPending ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Page>
  )
}
